import { useEffect, useRef, useState } from "react";
import {
  FILL_BLANK_TOKEN,
  collectUploadFiles,
  normalizeQuestionOptions,
  promptToHtml,
  responseIsComplete,
  shownToLearner,
  undecorateFillBlanks,
  type ChoiceOption,
  type MatchingPair,
  type UploadFileRef,
} from "@/lib/questionScoring";
import { errorMessage } from "@/lib/api";
import { uploadAnswerFile } from "@/lib/media";
import StudentMatchingBoard from "./StudentMatchingBoard";
import { ExternalLink, FileText, Loader2, Plus, Trash2 } from "lucide-react";

export type StudentQuestion = {
  id: string;
  prompt: string;
  type: string;
  options: Array<ChoiceOption | MatchingPair>;
  /** Lefts and rights as two lists. Present only for matching, and it does not say which left goes with which right. */
  matching?: { lefts: Array<{ id: number; text: string }>; rights: Array<{ id: number; text: string }> };
  presentation?: string | null;
  points: number;
  position: number;
  allowMultiple?: boolean;
  maxFiles?: number;
};

export type QuestionResponse = {
  optionId?: number;
  optionIds?: number[];
  answer?: string;
  blanks?: string[];
  pairs?: Record<string, number>;
  url?: string;
  urls?: string[];
  files?: UploadFileRef[];
};

export type QuestionResult = {
  questionId: string;
  isCorrect: boolean | null;
  /** The answer key. Present ONLY once the attempt is submitted — the server
   *  withholds it on drafts, so this is undefined until then. */
  correct?: { optionId?: number; optionIds?: number[]; answer?: string; pairs?: Record<string, number> };
  pointsEarned: number | null;
  pointsPossible: number | null;
  pending?: boolean;
  /** Left id → whether that one match was right. Only after submit, when incorrect answers are shown. */
  matchMarks?: Record<string, boolean>;
  /** What the teacher wrote. On a written answer this is the mark — the number
   *  alone does not tell a student what to do differently next time. */
  teacherComment?: string | null;
  gradedAt?: string | null;
};

type Props = {
  questions: StudentQuestion[];
  value: Record<string, QuestionResponse>;
  onChange: (questionId: string, response: QuestionResponse) => void;
  disabled?: boolean;
  results?: Record<string, QuestionResult>;
  showIncorrect?: boolean;
  showAnswerKey?: boolean;
  showQuestionPoints?: boolean;
  activityId?: string;
  submissionId?: string;
};

export default function StudentQuestions({
  questions,
  value,
  onChange,
  disabled,
  results,
  showIncorrect = false,
  showAnswerKey = false,
  showQuestionPoints = true,
  activityId,
  submissionId,
}: Props) {
  const [nudgeUnfinished, setNudgeUnfinished] = useState(false);
  useEffect(() => {
    if (disabled) {
      setNudgeUnfinished(false);
      return;
    }
    setNudgeUnfinished(false);
    const timer = window.setTimeout(() => setNudgeUnfinished(true), 60_000);
    return () => window.clearTimeout(timer);
  }, [disabled, activityId]);

  return (
    <div className="student-questions">
      {questions.filter(shownToLearner).map((question, index) => (
        <StudentQuestionField
          key={question.id}
          question={question}
          index={index}
          value={value[question.id] || {}}
          onChange={(next) => onChange(question.id, next)}
          disabled={disabled}
          result={results?.[question.id]}
          showIncorrect={showIncorrect}
          showAnswerKey={showAnswerKey}
          showQuestionPoints={showQuestionPoints}
          activityId={activityId}
          submissionId={submissionId}
          showUnfinished={nudgeUnfinished}
        />
      ))}
    </div>
  );
}

function StudentQuestionField({
  question,
  index,
  value,
  onChange,
  disabled,
  result,
  showIncorrect = false,
  showAnswerKey = false,
  showQuestionPoints = true,
  activityId,
  submissionId,
  showUnfinished = false,
}: {
  question: StudentQuestion;
  index: number;
  value: QuestionResponse;
  onChange: (next: QuestionResponse) => void;
  disabled?: boolean;
  result?: QuestionResult;
  showIncorrect?: boolean;
  showAnswerKey?: boolean;
  showQuestionPoints?: boolean;
  activityId?: string;
  submissionId?: string;
  showUnfinished?: boolean;
}) {
  const options = normalizeQuestionOptions(question.options, question.type, question.presentation);
  const choices = options.filter((row): row is ChoiceOption => "label" in row);
  const fillBlanks = question.type === "fill_blank";
  const written = question.type === "short_answer" || question.type === "long_answer";
  const promptHtml = studentPromptHtml(question.prompt, fillBlanks);
  const presentation = question.presentation;
  const dropdown = question.type === "single_choice" && presentation === "dropdown";
  const multi = question.type === "multiple_choice";
  const pts = Number(question.points) || 0;
  const blankCount = fillBlanks ? Math.max(1, countBlanks(question.prompt)) : 0;
  const blanks = Array.from({ length: blankCount }, (_, i) => value.blanks?.[i] ?? "");
  const unfinished =
    showUnfinished && !disabled && question.type !== "directions" && !responseIsComplete(question, value);

  const awaiting = Boolean(result && question.type !== "directions" && result.pending);
  const possible = Number(result?.pointsPossible ?? pts) || 0;
  const earned = result?.pointsEarned ?? 0;
  const showReceived =
    Boolean(result && question.type !== "directions" && !result.pending) &&
    Boolean(showQuestionPoints && result && result.pointsPossible != null);
  const unit = possible === 1 ? "point" : "points";
  const scoreLabel =
    question.type === "directions" || !possible
      ? ""
      : awaiting
        ? `?/${possible} ${unit}`
        : showReceived
          ? `${earned}/${possible} ${unit}`
          : `${possible} ${unit}`;
  const fullCredit =
    showReceived && possible > 0 && Number(result?.pointsEarned) === possible;
  const showMeta = Boolean(scoreLabel);

  return (
    <div className="student-question" id={`student-q-${question.id}`}>
      {unfinished ? (
        <span className="student-question__miss" title="Not finished" aria-label="Not finished" />
      ) : null}
      <div className="student-question__head">
        <span className="student-question__n">
          <span className="student-vh">Question </span>
          {index + 1}
        </span>
        {showMeta ? (
          <div className="student-question__score">
            <p className={`student-question__meta${fullCredit ? " is-full" : ""}`}>
              {scoreLabel}
            </p>
            {awaiting ? (
              <span className="student-pending-badge">Waiting for teacher</span>
            ) : null}
          </div>
        ) : null}
      </div>
      {fillBlanks ? (
        <FillBlankPrompt
          html={promptHtml}
          blanks={blanks}
          disabled={disabled}
          onChange={(next) => onChange({ blanks: next, answer: next.map((b) => b.trim()).filter(Boolean).join(" ") })}
        />
      ) : (
        <div className="student-question__prompt" dangerouslySetInnerHTML={{ __html: promptHtml }} />
      )}
      {question.type === "directions" ? null : question.type === "matching" ? (
        <StudentMatchingBoard
          lefts={question.matching?.lefts || []}
          rights={question.matching?.rights || []}
          value={value.pairs || {}}
          onChange={(pairs) => onChange({ pairs })}
          disabled={disabled}
          graded={Boolean(result) && !result?.pending && showIncorrect && result?.isCorrect != null}
          marks={showIncorrect ? result?.matchMarks : undefined}
          keyPairs={
            Boolean(result) && !result?.pending && showAnswerKey ? result?.correct?.pairs : undefined
          }
          seed={question.id}
        />
      ) : written && disabled ? (
        /* Once it is handed in, the answer is read back as text rather than
           left in a disabled input. A single-line input clips a long answer at
           the edge of the box, so a student could not re-read what they wrote —
           which is most of the point of getting the work back. */
        <div className="student-written">
          {value.answer ? String(value.answer) : <em>You left this one blank.</em>}
        </div>
      ) : question.type === "long_answer" ? (
        <textarea
          className="student-textarea"
          placeholder="Your answer"
          rows={4}
          value={value.answer ?? ""}
          onChange={(e) => onChange({ answer: e.target.value })}
        />
      ) : question.type === "short_answer" ? (
        <input
          className="student-input"
          placeholder="Your answer"
          value={value.answer ?? ""}
          onChange={(e) => onChange({ answer: e.target.value })}
        />
      ) : question.type === "number" ? (
        <input
          className="student-input"
          type="number"
          disabled={disabled}
          placeholder="0"
          value={value.answer ?? ""}
          onChange={(e) => onChange({ answer: e.target.value })}
        />
      ) : question.type === "date" ? (
        <input
          className="student-input"
          type="date"
          disabled={disabled}
          value={value.answer ?? ""}
          onChange={(e) => onChange({ answer: e.target.value })}
        />
      ) : question.type === "upload" ? (
        <UploadFields
          activityId={activityId}
          submissionId={submissionId}
          questionId={question.id}
          maxFiles={question.allowMultiple === false || question.maxFiles === 1 ? 1 : undefined}
          value={value}
          disabled={disabled}
          onChange={onChange}
        />
      ) : dropdown ? (
        <select
          className="student-input"
          disabled={disabled}
          value={value.optionId ?? ""}
          onChange={(e) => onChange(e.target.value ? { optionId: Number(e.target.value) } : {})}
        >
          <option value="">Choose…</option>
          {choices.map((choice) => (
            <option key={choice.id} value={choice.id}>
              {choice.label.trim() || "Option"}
            </option>
          ))}
        </select>
      ) : choices.length ? (
        <div className="student-choices">
          {choices.map((choice) => {
            const selected = multi
              ? (value.optionIds || []).includes(choice.id)
              : value.optionId === choice.id;

            // After submitting, mark what happened. `key` is undefined until
            // the attempt is turned in, so nothing is revealed before then.
            const key = result?.correct;
            const graded = Boolean(result) && !result?.pending;
            const isKey = key
              ? multi
                ? (key.optionIds || []).includes(choice.id)
                : key.optionId === choice.id
              : false;
            let mark = "";
            if (graded) {
              if (selected && showIncorrect && result?.isCorrect != null) {
                mark = result.isCorrect ? " is-correct" : " is-wrong";
              } else if (showAnswerKey && isKey) {
                mark = " is-key";
              }
            }

            return (
              <button
                key={choice.id}
                type="button"
                className={`student-choice${selected ? " is-selected" : ""}${mark}`}
                disabled={disabled}
                onClick={() => {
                  if (multi) {
                    const set = new Set(value.optionIds || []);
                    if (set.has(choice.id)) set.delete(choice.id);
                    else set.add(choice.id);
                    onChange({ optionIds: [...set] });
                  } else {
                    onChange({ optionId: choice.id });
                  }
                }}
              >
                <span>{choice.label.trim() || "Option"}</span>
                <span className="student-choice__mark">
                  {mark === " is-correct" ? (
                    <span className="student-choice__tag">Your answer · correct</span>
                  ) : mark === " is-wrong" ? (
                    <span className="student-choice__tag">Your answer</span>
                  ) : mark === " is-key" ? (
                    <span className="student-choice__tag">Correct answer</span>
                  ) : null}

                  <span aria-hidden>{multi ? (selected ? "☑" : "☐") : selected ? "●" : "○"}</span>
                </span>
              </button>
            );
          })}
        </div>
      ) : null}

      {/* The teacher's note. On a written answer this IS the mark — a bare
          number does not tell a child what to do differently next time. It sits
          below the answer rather than in the meta line so it can be a sentence,
          and it is attributed so a student knows a person wrote it. */}
      {result?.teacherComment ? (
        <div className="student-feedback">
          <span className="student-feedback__who">Your teacher wrote</span>
          <p>{result.teacherComment}</p>
        </div>
      ) : null}
    </div>
  );
}

function toUploadResponse(files: UploadFileRef[]): QuestionResponse {
  const kept = files.filter((row) => row.url || row.s3Key);
  const urls = kept.map((row) => row.url).filter(Boolean);
  return urls.length ? { files: kept, urls, url: urls[0] } : { files: [], urls: [] };
}

function isImageThumb(name: string, mime?: string, url?: string) {
  if (String(mime || "").startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(`${name} ${url || ""}`);
}

function isVideoThumb(name: string, mime?: string, url?: string) {
  if (String(mime || "").startsWith("video/")) return true;
  return /\.(mp4|webm|mov|m4v|ogv)(\?|$)/i.test(`${name} ${url || ""}`);
}

function localPreviewUrl(file: File) {
  if (isImageThumb(file.name, file.type) || isVideoThumb(file.name, file.type)) {
    return URL.createObjectURL(file);
  }
  return "";
}

function showVideoFrame(video: HTMLVideoElement | null) {
  if (!video) return;
  const punch = () => {
    if (video.currentTime === 0) video.currentTime = 0.12;
  };
  if (video.readyState >= 1) punch();
  else video.addEventListener("loadedmetadata", punch, { once: true });
}

function UploadFields({
  activityId,
  submissionId,
  questionId,
  maxFiles,
  value,
  disabled,
  onChange,
}: {
  activityId?: string;
  submissionId?: string;
  questionId: string;
  maxFiles?: number;
  value: QuestionResponse;
  disabled?: boolean;
  onChange: (next: QuestionResponse) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const controllersRef = useRef(new Map<string, AbortController>());
  const cancelledRef = useRef(new Set<string>());
  const thumbsRef = useRef(new Map<string, string>());
  const [pending, setPending] = useState<
    Array<{ id: string; name: string; error?: string; preview?: string; mime?: string }>
  >([]);
  const multiple = maxFiles !== 1;
  const files = collectUploadFiles(value);
  const filesRef = useRef(files);
  filesRef.current = files;
  const canAttach = !disabled && Boolean(activityId);

  useEffect(() => {
    const thumbs = thumbsRef.current;
    return () => {
      thumbs.forEach((url) => URL.revokeObjectURL(url));
      thumbs.clear();
    };
  }, []);

  const pick = () => {
    if (!canAttach) return;
    inputRef.current?.click();
  };

  const forgetPreview = (id: string, extra?: string) => {
    const stored = thumbsRef.current.get(id);
    if (stored) {
      URL.revokeObjectURL(stored);
      thumbsRef.current.delete(id);
    }
    if (extra) URL.revokeObjectURL(extra);
  };

  const attach = async (list: FileList | null) => {
    if (!activityId || disabled || !list?.length) return;
    const take = multiple ? [...list] : [...list].slice(0, 1);
    if (!take.length) return;
    const jobs = take.map((file) => ({
      id: crypto.randomUUID(),
      name: file.name,
      file,
      preview: localPreviewUrl(file),
      mime: file.type,
    }));
    setPending((prev) => [
      ...prev.filter((row) => !row.error),
      ...jobs.map(({ id, name, preview, mime }) => ({ id, name, preview, mime })),
    ]);
    for (const job of jobs) {
      const ac = new AbortController();
      controllersRef.current.set(job.id, ac);
      try {
        if (cancelledRef.current.has(job.id)) continue;
        const row = await uploadAnswerFile({
          activityId,
          questionId,
          submissionId,
          file: job.file,
          signal: ac.signal,
        });
        if (cancelledRef.current.has(job.id)) {
          if (job.preview) URL.revokeObjectURL(job.preview);
          continue;
        }
        if (job.preview) thumbsRef.current.set(row.id, job.preview);
        const next = multiple ? [...filesRef.current, row] : [row];
        filesRef.current = next;
        onChange(toUploadResponse(next));
        setPending((prev) => prev.filter((item) => item.id !== job.id));
      } catch (err) {
        if (cancelledRef.current.has(job.id) || (err instanceof Error && err.name === "AbortError")) {
          if (job.preview) URL.revokeObjectURL(job.preview);
          continue;
        }
        setPending((prev) =>
          prev.map((item) => (item.id === job.id ? { ...item, error: errorMessage(err) } : item)),
        );
      } finally {
        controllersRef.current.delete(job.id);
      }
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="student-uploads">
      <input
        ref={inputRef}
        className="student-vh"
        type="file"
        multiple={multiple}
        disabled={!canAttach}
        onChange={(e) => void attach(e.target.files)}
      />
      {files.map((file, index) => (
        <UploadTile
          key={file.id || file.s3Key || file.url}
          name={file.name || `File ${index + 1}`}
          href={file.url}
          preview={thumbsRef.current.get(file.id) || ""}
          mime={file.mime}
          disabled={disabled}
          onRemove={
            disabled
              ? undefined
              : () => {
                  forgetPreview(file.id);
                  onChange(toUploadResponse(files.filter((_, i) => i !== index)));
                }
          }
        />
      ))}
      {pending.map((row) => (
        <UploadTile
          key={row.id}
          name={row.name}
          preview={row.preview}
          mime={row.mime}
          busy={!row.error}
          error={row.error}
          onRemove={() => {
            cancelledRef.current.add(row.id);
            controllersRef.current.get(row.id)?.abort();
            forgetPreview(row.id, row.preview);
            setPending((prev) => prev.filter((item) => item.id !== row.id));
          }}
        />
      ))}
      {canAttach ? (
        <button type="button" className="student-upload-tile student-upload-tile--ghost" onClick={pick}>
          <Plus size={22} aria-hidden />
          <span>
            {multiple
              ? files.length
                ? "Add another"
                : "Attach files"
              : files.length
                ? "Replace"
                : "Attach file"}
          </span>
        </button>
      ) : null}
    </div>
  );
}

function UploadTile({
  name,
  href,
  preview,
  mime,
  busy,
  error,
  disabled,
  onRemove,
}: {
  name: string;
  href?: string;
  preview?: string;
  mime?: string;
  busy?: boolean;
  error?: string;
  disabled?: boolean;
  onRemove?: () => void;
}) {
  const src = preview || href || "";
  const image = src && isImageThumb(name, mime, src) ? src : "";
  const video = !image && src && isVideoThumb(name, mime, src) ? src : "";
  const [broken, setBroken] = useState(false);

  return (
    <div className={`student-upload-tile${error ? " is-error" : ""}${busy ? " is-busy" : ""}`}>
      {image && !broken ? (
        <img className="student-upload-tile__media" src={image} alt="" onError={() => setBroken(true)} />
      ) : video ? (
        <video
          className="student-upload-tile__media"
          src={video}
          muted
          playsInline
          preload="auto"
          ref={showVideoFrame}
        />
      ) : (
        <span className="student-upload-tile__fallback">
          <FileText size={22} aria-hidden />
        </span>
      )}
      <span className="student-upload-tile__name">{error || name}</span>
      {busy ? (
        <span className="student-upload-tile__status">
          <Loader2 size={16} className="student-upload-spin" aria-hidden />
        </span>
      ) : null}
      <span className="student-upload-tile__bar">
        {href ? (
          <a
            className="student-upload-tile__act"
            href={href}
            target="_blank"
            rel="noreferrer"
            aria-label={`Open ${name} in a new tab`}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={14} />
          </a>
        ) : null}
        {onRemove && !disabled ? (
          <button
            type="button"
            className="student-upload-tile__act"
            aria-label={busy ? `Cancel upload of ${name}` : `Remove ${name}`}
            onClick={onRemove}
          >
            <Trash2 size={14} />
          </button>
        ) : null}
      </span>
    </div>
  );
}

function FillBlankPrompt({
  html,
  blanks,
  disabled,
  onChange,
}: {
  html: string;
  blanks: string[];
  disabled?: boolean;
  onChange: (blanks: string[]) => void;
}) {
  const parts = html.split(FILL_BLANK_TOKEN);
  return (
    <div className="student-question__prompt">
      {parts.map((part, i) => (
        <span key={i}>
          <span dangerouslySetInnerHTML={{ __html: part }} />
          {i < parts.length - 1 ? (
            <input
              className="student-blank"
              disabled={disabled}
              aria-label={`Blank ${i + 1}`}
              value={blanks[i] ?? ""}
              onChange={(e) => {
                const next = [...blanks];
                next[i] = e.target.value;
                onChange(next);
              }}
            />
          ) : null}
        </span>
      ))}
    </div>
  );
}

function studentPromptHtml(value: string, fillBlanks: boolean) {
  const html = promptToHtml(value) || "<p>(No prompt)</p>";
  if (!fillBlanks) return html;
  return undecorateFillBlanks(html);
}

function countBlanks(value: string) {
  return (undecorateFillBlanks(String(value || "")).match(/\$\{blank\}/g) || []).length;
}

